import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { useQuery } from '@tanstack/react-query';
import AdminSearchResultsGrid from 'components/admin/search/AdminSearchResultsGrid';
import { FieldPanelFullWidth } from 'styles/v2/Styles.styled';
import { useIndividualVerifierSearch } from 'hooks/useVerifierSearchHook';
import useDebounce from 'hooks/useDebounce';
import { getIndividualVerifierOrganisations } from 'services/api/verifiersApi';
import { useCountriesLookup } from 'services/api/queries';
import { IndividualVerifierListItem } from 'types/verifiers/VerifierModels';
import EmailCell from '../components/EmailCell';
import TruncatedCell from '../components/TruncatedCell';
import FilterSelect from '../../../components/filters/FilterSelect';
import SearchInput from '../../../components/filters/SearchInput';
import { Option } from 'types/types';
import NoResultsFound from '../components/NoResultsFound';
import { GLOBAL_OPTION_VALUE, PAGE_SIZES, SKELETON_ROWS } from '../constants';
import { Container, SearchPanel, FilterRow, FilterField, SearchFilterField } from './tabStyles';

const IndividualVerifiersTab: React.FC = () => {
  const { isLoading, searchResult, onSearch, pagination, onPageChange } = useIndividualVerifierSearch();

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 2000);
  const [selectedOrganisation, setSelectedOrganisation] = useState<Option | null>(null);
  const [selectedMarketplace, setSelectedMarketplace] = useState<Option[]>([]);

  const { data: organisations = [] } = useQuery({
    queryKey: ['verifiers', 'individual', 'organisations'],
    queryFn: getIndividualVerifierOrganisations,
    staleTime: 5 * 60 * 1000,
  });

  const { data: countries = [] } = useCountriesLookup();

  const organisationOptions = useMemo(() => organisations.map((o) => ({ label: o, value: o })), [organisations]);

  const marketplaceOptions = useMemo(
    () => [
      { label: 'Global', value: GLOBAL_OPTION_VALUE },
      ...countries.map((c) => ({ label: c.name ?? '', value: c.id ?? '' })),
    ],
    [countries]
  );

  const skipNextDebounce = useRef(false);

  useEffect(() => {
    if (skipNextDebounce.current) {
      skipNextDebounce.current = false;
      return;
    }
    const selectedValues = selectedMarketplace.map((o) => o.value);
    const countryIds = selectedValues.filter((v) => v !== GLOBAL_OPTION_VALUE);
    onSearch({
      searchTerm: debouncedSearchTerm || undefined,
      organisation: selectedOrganisation?.value || undefined,
      countryIds: countryIds.length > 0 ? countryIds : undefined,
      includeGlobal: selectedValues.includes(GLOBAL_OPTION_VALUE),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, selectedOrganisation, selectedMarketplace]);

  const handleClearSearch = useCallback(() => {
    skipNextDebounce.current = true;
    setSearchTerm('');
    const selectedValues = selectedMarketplace.map((o) => o.value);
    const countryIds = selectedValues.filter((v) => v !== GLOBAL_OPTION_VALUE);
    onSearch({
      searchTerm: undefined,
      organisation: selectedOrganisation?.value || undefined,
      countryIds: countryIds.length > 0 ? countryIds : undefined,
      includeGlobal: selectedValues.includes(GLOBAL_OPTION_VALUE),
    });
  }, [selectedMarketplace, selectedOrganisation, onSearch]);

  const emailBodyTemplate = useCallback((row: IndividualVerifierListItem) => <EmailCell email={row.email} />, []);
  const verifierNameBodyTemplate = useCallback(
    (row: IndividualVerifierListItem) => <TruncatedCell value={row.verifierName} />,
    []
  );
  const organisationBodyTemplate = useCallback(
    (row: IndividualVerifierListItem) => <TruncatedCell value={row.organisation} />,
    []
  );
  const marketplaceBodyTemplate = useCallback(
    (row: IndividualVerifierListItem) => <TruncatedCell value={row.marketplace} />,
    []
  );

  return (
    <Container>
      <SearchPanel>
        <FilterRow>
          <SearchFilterField>
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClear={handleClearSearch}
              placeholder="Type name or email here"
            />
          </SearchFilterField>
          <FilterField>
            <FilterSelect
              value={selectedOrganisation}
              options={organisationOptions}
              onChange={setSelectedOrganisation}
              label="Organisation"
            />
          </FilterField>
          <FilterField>
            <FilterSelect
              isMulti={true}
              options={marketplaceOptions}
              value={selectedMarketplace}
              onChange={setSelectedMarketplace}
              label="Marketplace"
            />
          </FilterField>
        </FilterRow>
      </SearchPanel>

      {isLoading && !searchResult ? (
        <FieldPanelFullWidth>
          <AdminSearchResultsGrid
            value={SKELETON_ROWS}
            totalRecords={0}
            pageIndex={0}
            pageSize={PAGE_SIZES[0]}
            onPageChange={() => {}}
            pageSizeOptions={PAGE_SIZES}
            style={{ width: '100%' }}
            tableStyle={{ minWidth: '40rem', tableLayout: 'fixed' }}
          >
            <Column field="verifierName" header="Verifier name" style={{ width: 260 }} body={<Skeleton />} />
            <Column field="organisation" header="Organisation" style={{ width: 275 }} body={<Skeleton />} />
            <Column field="marketplace" header="Marketplace" style={{ width: 228 }} body={<Skeleton />} />
            <Column field="email" header="Contact email" style={{ width: 275 }} body={<Skeleton />} />
          </AdminSearchResultsGrid>
        </FieldPanelFullWidth>
      ) : (
        searchResult &&
        (searchResult.totalCount > 0 ? (
          <FieldPanelFullWidth>
            <AdminSearchResultsGrid
              value={searchResult.items}
              totalRecords={searchResult.totalCount}
              pageIndex={pagination.pageIndex}
              pageSize={pagination.pageSize}
              onPageChange={onPageChange}
              pageSizeOptions={PAGE_SIZES}
              loading={isLoading}
              style={{ width: '100%' }}
              tableStyle={{ minWidth: '40rem', tableLayout: 'fixed' }}
            >
              <Column field="verifierName" header="Verifier name" style={{ width: 260 }} body={verifierNameBodyTemplate} />
              <Column field="organisation" header="Organisation" style={{ width: 275 }} body={organisationBodyTemplate} />
              <Column field="marketplace" header="Marketplace" style={{ width: 228 }} body={marketplaceBodyTemplate} />
              <Column field="email" header="Contact email" style={{ width: 275 }} body={emailBodyTemplate} />
            </AdminSearchResultsGrid>
          </FieldPanelFullWidth>
        ) : (
          <NoResultsFound />
        ))
      )}
    </Container>
  );
};

export default IndividualVerifiersTab;
